import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '@/i18n';

export type Language = 'en' | 'si' | 'ta';

export const LANGUAGE_CODES = ['en', 'si', 'ta'] as const;

/** localStorage key for the persisted language. Shared with the i18n detector
 *  in `src/i18n/index.ts` and the pre-mount script in `index.html`. */
export const LANGUAGE_STORAGE_KEY = 'synapse.language';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value);
}

export const LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'si', label: 'Sinhala', nativeLabel: 'සිංහල' },
  { code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்' },
];

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

function applyLanguageEffects(lang: Language) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
  if (i18n.language !== lang) {
    void i18n.changeLanguage(lang);
  }
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: 'en',
      setLanguage: (language) => {
        applyLanguageEffects(language);
        set({ language });
      },
    }),
    {
      name: LANGUAGE_STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        if (state?.language) applyLanguageEffects(state.language);
      },
    },
  ),
);
